import { Spinner } from '@heroui/react';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { ErrorCard } from '../../../../components/ErrorCard';
import { useNotification } from '../../../../contexts/NotificationContext';
import { useGetUser, useUpdateUser } from '../../hooks';
import { buildUserUpdateInput } from '../../utils/buildUserUpdateInput';
import { isSsoLoginMethod } from '../../utils/loginMethodDisplay';
import { DeleteUser } from '../DeleteUser';
import { UserFormData } from '../UserForm/hooks/useUserFormValidation';
import { UserFormChangedFields } from '../UserForm/utils/changedFields';

import {
  ErrorDisplay,
  UserEditForm,
  UserInfoCards,
  UserProfileHeader,
} from './components';

export const UserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const [error, setError] = useState<string | null>(null);

  const {
    user,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useGetUser({
    userId: userId || '',
    skip: !userId,
    captureException: (queryErr) => {
      addNotification({
        title: 'Error Loading User',
        message: 'Failed to load user profile. Please try again.',
        type: 'error',
      });
    },
  });

  const { updateUser, isLoading: isSubmitting } = useUpdateUser({
    captureException: (updateErr) => {
      addNotification({
        title: 'Error Updating User',
        message:
          updateErr.message || 'Failed to update user. Please try again.',
        type: 'error',
      });
    },
  });

  useEffect(() => {
    if (user) {
      document.title = `${user.name} - User Profile - Choco Admin`;
    }
  }, [user]);

  const handleBack = () => {
    navigate('/users');
  };

  const isEmailLoginUser =
    user?.loginMethod === 'email' || user?.loginMethod === 'emailAndPhone';

  const initialFormData: Partial<UserFormData> = user
    ? {
        name: user.name || '',
        // For email-login users the form's email field drives the login
        // credential (loginEmail in GraphQL); seed it from loginEmail so the
        // current credential is visible and editable.
        email: isEmailLoginUser
          ? (user.loginEmail ?? user.email ?? '')
          : (user.email ?? ''),
        phone: user.phone || '',
        locale: user.locale || 'en-US',
        isTest: user.isTest || false,
        supplier: user.supplier || false,
        loginMethod: user.loginMethod || 'phone',
        supportLogin: user.supportLoginEnabled || false,
        orderPreferences: user.orderPreferences || {
          pdf: false,
          xlsx: false,
          sms: null,
          whatsApp: null,
        },
      }
    : {};

  const handleSubmit = async (
    values: UserFormData,
    changedFields: UserFormChangedFields
  ) => {
    if (!userId || !user) return;

    setError(null);

    try {
      // Only the fields the admin actually touched are sent (see
      // buildUserUpdateInput) — an untouched, unrendered order-preferences
      // section for a buyer user is never included, since UserForm already
      // clears `values.orderPreferences` to `undefined` when the section is
      // hidden, and `useUpdateUser` only merges/sends it when it is present.
      const updateInput = buildUserUpdateInput({
        userId,
        values,
        changedFields,
      });

      // Pass the loaded user's current preferences so the hook can merge
      // them with the form values — required by the backend PutItem contract.
      await updateUser(updateInput, user.orderPreferences);

      // Profile picture uploads happen independently, straight to the
      // presigned uploadUrl, when the admin clicks "Save Picture" — see
      // useProfilePicture.
      addNotification({
        title: 'Success',
        message: 'User updated successfully',
        type: 'success',
      });

      // Refresh the user data to show updated information
      refetch();
    } catch (updateError) {
      // User update failed - extract error message and show to user
      let errorMessage = 'Failed to update user. Please try again.';
      if (updateError instanceof Error) {
        errorMessage = updateError.message;
      }

      setError(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="h-full overflow-auto p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <Spinner size="lg" color="primary" />
              <p className="text-default-500 mt-4">Loading user profile...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (queryError || !user) {
    return (
      <div className="h-full overflow-auto p-6">
        <div className="max-w-4xl mx-auto">
          <ErrorCard
            title="Error Loading User"
            message={
              queryError
                ? queryError.message || 'Error loading user'
                : 'User not found or could not be loaded'
            }
            backLinkTo="/users"
            backLinkText="← Back to Users"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <UserProfileHeader user={user} onBack={handleBack} />

        <UserInfoCards user={user} />

        {error && <ErrorDisplay error={error} />}

        <UserEditForm
          initialFormData={initialFormData}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          user={user}
          disabled={user.hasRequestedToBeDeleted || false}
        />

        <DeleteUser
          userId={user.id}
          userName={user.name}
          hasRequestedToBeDeleted={user.hasRequestedToBeDeleted || false}
          isSsoUser={isSsoLoginMethod(user.loginMethod)}
          onDeleted={() => {
            // Refresh user data after deletion to show updated status
            refetch();
          }}
          disabled={isSubmitting}
        />
      </div>
    </div>
  );
};
